import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ROUTES } from '../../../utils/constants';
import { createShift, getAdminShifts } from '../../../services/volunteerApi';
import { CreateShiftData } from '../../../types/volunteer';
import api from '../../../services/api';

interface RoleEntry {
  name: string;
  capacity: string;
}

const CreateVolunteerShiftPage: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState<{ id: string; title: string }[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [postcode, setPostcode] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    capacity: '',
    eventId: '',
  });

  const [roles, setRoles] = useState<RoleEntry[]>([]);

  useEffect(() => {
    // Fetch events and existing shifts, then filter out events that already have a linked shift
    Promise.all([
      api.get('/events?hidePast=true'),
      getAdminShifts(),
    ]).then(([eventsRes, shiftsRes]) => {
      const linkedEventIds = new Set(
        shiftsRes.data.filter((s: any) => s.eventId).map((s: any) => s.eventId)
      );
      setEvents(
        eventsRes.data
          .filter((e: any) => !linkedEventIds.has(e.id))
          .map((e: any) => ({ id: e.id, title: e.title }))
      );
    }).catch(() => {});
    api.get('/locations').then((res) => setLocations(res.data)).catch(() => {});
  }, []);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleAddRole = () => {
    setRoles([...roles, { name: '', capacity: '' }]);
  };

  const handleRoleChange = (index: number, field: keyof RoleEntry, value: string) => {
    const updated = [...roles];
    updated[index] = { ...updated[index], [field]: value };
    setRoles(updated);
  };

  const handleRemoveRole = (index: number) => {
    setRoles(roles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.date || !formData.startTime || !formData.endTime || !formData.location || !postcode || !formData.capacity) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const data: CreateShiftData = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: formData.location,
        postcode,
        capacity: Number(formData.capacity),
        eventId: formData.eventId || undefined,
        roles: roles
          .filter((r) => r.name.trim())
          .map((r) => ({
            name: r.name.trim(),
            capacity: r.capacity ? Number(r.capacity) : undefined,
          })),
      };

      await createShift(data);
      setShowSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.ADMIN_VOLUNTEER_SHIFTS);
      }, 1500);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to create shift. Please try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.ADMIN_VOLUNTEER_SHIFTS)}
        sx={{ mb: 3 }}
      >
        Back to Shifts
      </Button>

      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Create Volunteer Shift
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Fill in the details to create a new volunteer shift
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  value={formData.title}
                  onChange={handleChange('title')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={locations}
                  getOptionLabel={(option: any) => typeof option === 'string' ? option : option.name}
                  value={formData.location}
                  onChange={(_e, newValue) => {
                    if (typeof newValue === 'string') {
                      setFormData({ ...formData, location: newValue });
                    } else if (newValue) {
                      setFormData({ ...formData, location: newValue.name });
                      if (newValue.postcode) setPostcode(newValue.postcode);
                    } else {
                      setFormData({ ...formData, location: '' });
                    }
                  }}
                  onInputChange={(_e, newInput) => setFormData({ ...formData, location: newInput })}
                  renderInput={(params) => (
                    <TextField {...params} label="Location" required fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postcode"
                  required
                  placeholder="e.g., SW1A 1AA"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Link to Event (optional)"
                  value={formData.eventId}
                  onChange={handleChange('eventId')}
                >
                  <MenuItem value="">None</MenuItem>
                  {events.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.title}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Date & Time */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                  Date & Time
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formData.date}
                  onChange={handleChange('date')}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formData.startTime}
                  onChange={handleChange('startTime')}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formData.endTime}
                  onChange={handleChange('endTime')}
                />
              </Grid>

              {/* Capacity */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                  Capacity
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Volunteers"
                  required
                  value={formData.capacity}
                  onChange={handleChange('capacity')}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>

              {/* Roles */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Roles
                  </Typography>
                  <Button
                    startIcon={<AddCircleIcon />}
                    onClick={handleAddRole}
                    size="small"
                  >
                    Add Role
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Optionally define specific roles for this shift
                </Typography>
              </Grid>

              {roles.map((role, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Role Name"
                      value={role.name}
                      onChange={(e) => handleRoleChange(index, 'name', e.target.value)}
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      label="Capacity (optional)"
                      type="number"
                      value={role.capacity}
                      onChange={(e) => handleRoleChange(index, 'capacity', e.target.value)}
                      sx={{ flex: 1 }}
                      InputProps={{ inputProps: { min: 1 } }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveRole(index)}
                      title="Remove Role"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}

              {/* Submit */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Shift'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate(ROUTES.ADMIN_VOLUNTEER_SHIFTS)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Volunteer shift created successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateVolunteerShiftPage;
